import './index.less'

import 'assets/icons.font'
import 'assets/images/favicon.png'
import 'assets/images/share-image.jpg'
import en from 'copy/en'
import {createBrowserHistory} from 'history'
import {EventModel} from 'models/EventModel'
import {
	Demos,
	Error404,
	EventPage,
	Faq,
	Page,
	Program,
	Registration,
	Speakers,
	Subevent
} from 'pages'
import {Floorplan} from 'pages/floorplan/floorplan'
import {Home} from 'pages/home/home'
import queryString from 'query-string'
import React, {useEffect, useState} from 'react'
import {Route, Router, Switch} from 'react-router'
import useSWR from 'swr'
import fetch from 'unfetch'
import useRouter from 'use-react-router'
import {AuthContextProvider} from 'util/authcontext'
import {EventContextProvider} from 'util/eventcontext'
import {I18n} from 'util/i18n'
import {ScrollMemory} from 'util/scrollmemory'
import {TrackingContext} from 'util/trackers'
import {CookiehubContextProvider} from 'util/useCookiehub'
import {Events} from './pages/events/events'
import {EntryContext} from './util/entrycontext'

const pages = {
	home: Home,
	demos: Demos,
	error404: Error404,
	event: EventPage,
	faq: Faq,
	floorplan: Floorplan,
	page: Page,
	program: Program,
	program_multi: Program,
	registration: Registration,
	registration_onsite: Registration,
	subevent: Subevent,
	speakers: Speakers
}

// tslint:disable:no-string-literal
const {_general: generalData, _event: initialEvent, ...initialData} = window[
	'__INITIAL_STATE__'
]

const history = createBrowserHistory()

const fetcher = url => {
	if (url === initialData.url) return initialData
	return fetch(url + '?json').then(r => r.json())
}

const App: React.FC = () => {
	const [event, setEvent] = useState(
		initialEvent && new EventModel(initialEvent)
	)

	return (
		<I18n language="en" data={en}>
			<Router history={history}>
				<Switch>
					<Route path={`${event?.url}(/*)`}>
						{({location}) => (
							<EventContextProvider event={event}>
								<AuthContextProvider>
									<PageFetcher
										event={event}
										setEvent={setEvent}
										location={location}
									/>
								</AuthContextProvider>
							</EventContextProvider>
						)}
					</Route>
					<Route>
						{() => {
							if (initialData?.channel == 'home') {
								return <Home {...initialData} />
							}
							if (initialData?.channel == 'events_overview') {
								return <Events {...initialData} />
							}

							location.reload()
							return null
						}}
					</Route>
				</Switch>
			</Router>
		</I18n>
	)
}

const PageFetcher: React.FC<{
	event: EventModel
	setEvent: (EventModel) => void
	location: any
}> = attrs => {
	const {
		event,
		setEvent,
		location: {pathname}
	} = attrs

	const queryParams = queryString.parse(location.search)
	if (queryParams.status && queryParams.status === 'success') {
		window.sessionStorage.clear()
	}

	const router = useRouter()
	const [prevData, setPrevData] = useState<any>(null)
	const [currentLocation, setCurrentLocation] = useState<any>(router.location)
	const {data, error} = useSWR<any>(pathname, fetcher, {
		refreshInterval: 0,
		revalidateOnFocus: false,
		focusThrottleInterval: 99999
	})

	useEffect(() => {
		if (data !== prevData) {
			// Update location if new data is fetched
			setPrevData(data)
			if (data?.metadata?.title) document.title = data.metadata.title
			setCurrentLocation(router.location)
		} else if (router.location.pathname === pathname) {
			// Update location if the hash changed (in order to scroll to the anchor)
			setCurrentLocation(router.location)
		}
	}, [data, location.hash])

	useEffect(() => {
		if (error) {
			// Reload the page
			location.reload()
		}
	}, [error])

	// setCurrentLocation(router.location)

	if (error) {
		console.error(error)
		return null
	}
	if (!data && !prevData) return null

	const currentData = data || prevData
	const {channel, ...entry} = currentData
	const View = pages[channel] || Error404

	return (
		<EntryContext.Provider
			value={{channel, id: currentData.id, title: currentData.title}}
		>
			<CookiehubContextProvider>
				<TrackingContext.Provider
					value={{url: currentData.url, title: currentData.title}}
				>
					<ScrollMemory history={history} location={currentLocation} />
					<View {...entry} channel={channel} />
				</TrackingContext.Provider>
			</CookiehubContextProvider>
		</EntryContext.Provider>
	)
}

export default App
